import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {
  Button,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useDataProvider,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import { delay } from "utils/tools";
import SaveIcon from "@material-ui/icons/Save";
import TableResultConfirmAdjustmentPrices from "./TableResultConfirmAdjustmentPrices";
import { ERejectionReason, rejectionReasonList, remarkTypeFormatter } from "../const";

interface IBulkRejectProps {
  filteredData: ISubcontractorAdjustmentPrice[];
  basePath: string;
  filterValues: any;
  label: string;
}

interface IRejectResults {
  id: number;
  routeName: string;
  status: string;
}

const useStyles = makeStyles({
  actionColor: {
    color: "#FFF",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  form: {
    "& .MuiCardContent-root": {
      padding: 0,
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

const BulkActionRejectButton = ({ filteredData }: IBulkRejectProps) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<IRejectResults[]>([]);
  const [processing, setProcessing] = useState(false);
  const [remark, setRemark] = useState<string>(""); // This is remark will be submitted
  const [selectedReason, setSelectedReason] = useState<ERejectionReason>(null); // Selected reason from dropdown
  const [otherRemarkText, setOtherRemarkText] = useState<string>(""); // Raw text for 'Other' field
  const [invalid, setInvalid] = useState<boolean>(true); // Button disable flag

  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  // enable/disable confirm button based on remark dropdown/other remark value
  useEffect(() => {
    const isRemarkValid = !!selectedReason;
    const isOtherSelected = selectedReason === ERejectionReason.other;
    const isOtherRemarkValid = !!otherRemarkText && otherRemarkText.trim() !== "";

    // Button is invalid if no remark selected or "Other" has empty text
    const isInvalid = !isRemarkValid || (isOtherSelected && !isOtherRemarkValid);

    setInvalid(isInvalid);
  }, [selectedReason, otherRemarkText]);

  const selectedAdjustmentPrices = filteredData.map((data) => ({
    id: data.id,
    routeName: data.routeName,
    status: data.status,
  }));

  const selectedRouteNames = filteredData.map((data) => data.routeName);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      unselectAll("subcontractorAdjustmentPrices");
      refresh();
    }

    setOpen(false);
  };

  const handleReject = (
    selectedAdjustmentPrice: IRejectResults,
    index: number,
  ) => {
    provider
      .customUpdate(
        `subcontractorAdjustmentPrices/${selectedAdjustmentPrice.id}/reject`,
        {
          data: { remark: remark },
        },
      )
      .then(() => {
        let tempAdjustments = selectedAdjustmentPrices;
        tempAdjustments[index].status = "Rejected";
        setResults([...tempAdjustments]);
      })
      .catch((err: any) => {
        let tempAdjustments = selectedAdjustmentPrices;
        tempAdjustments[index].status = `Error, ${err.message}`;
        setResults([...tempAdjustments]);
      });
  };

  const handleConfirm = async (selectedAdjustmentPrices: IRejectResults[]) => {
    setProcessing(true);

    selectedAdjustmentPrices.map((selectedAdjustmentPrice) => {
      selectedAdjustmentPrice.status = "Loading...";
    });

    for (const selectedAdjustmentPrice of selectedAdjustmentPrices) {
      const index = selectedAdjustmentPrices.indexOf(selectedAdjustmentPrice);

      handleReject(selectedAdjustmentPrice, index);
      await delay(2000);
    }

    setFinished(true);
    setProcessing(false);
  };

  return (
    <Fragment>
      <Button
        label="Reject Selected"
        onClick={handleOpen}
        style={{ backgroundColor: "#DA4040" }}
      />

      <SimpleForm toolbar={null} className={classes.form}>
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
          maxWidth="lg"
        >
          <DialogTitle id="alert-dialog-title">
            Reject Selected Adjustment Prices
          </DialogTitle>
          <DialogContent>
            {results.length > 0 ? (
              <TableResultConfirmAdjustmentPrices records={results} />
            ) : (
              <>
                <DialogContentText color="initial">
                  <Typography variant="body1" gutterBottom>
                    You are about to reject the following adjustment price for
                    routes: {selectedRouteNames.join(", ")} <br />
                    Please provide a reason for this action.
                  </Typography>
                </DialogContentText>

                <SelectInput
                  source="remark"
                  choices={rejectionReasonList}
                  validate={required()}
                  onChange={(e: any) => {
                    const value = e.target.value as ERejectionReason;
                    setSelectedReason(value);

                    // Set prefilled remark or set to empty for 'Other'
                    if (value !== ERejectionReason.other) {
                      setRemark(remarkTypeFormatter(value));
                      setOtherRemarkText(""); // clear if switching from 'Other'
                    } else {
                      setRemark("");
                    }
                  }}
                />

                {selectedReason === ERejectionReason.other && (
                  <TextInput
                    label="Remark Text"
                    source="otherRemark"
                    validate={required()}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      setOtherRemarkText(value); // set raw input, without prefix
                      setRemark(`Other: ${value}`); // Construct final remark string, include prefix
                    }}
                  />
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {!finished && (
              <Button
                onClick={() => handleConfirm(selectedAdjustmentPrices)}
                color="primary"
                label="Confirm"
                disabled={processing || invalid}
              >
                <SaveIcon />
              </Button>
            )}
            <Button
              onClick={handleClose}
              color="secondary"
              label={`${finished ? "Finish" : "Back"}`}
              disabled={processing}
              className={classes.cancelButton}
            />
          </DialogActions>
        </Dialog>
      </SimpleForm>
    </Fragment>
  );
};

export default BulkActionRejectButton;
